import React, { useState, useEffect } from 'react';
import { Target, Layers, Brain, Activity, Zap, FileText, ChevronRight, CheckCircle, Package } from 'lucide-react';
import { useFadeIn } from '../hooks/useFadeIn';
import './ProductsPage.css';

interface ProductsPageProps {
  onLoginClick?: () => void;
}

// Section wrapper component for fade-in
const SectionFadeIn: React.FC<{ children: React.ReactNode; delay: number }> = ({ children, delay }) => {
  const fadeIn = useFadeIn<HTMLDivElement>({ delay });
  return (
    <div 
      ref={fadeIn.ref} 
      className={fadeIn.isVisible ? 'fade-in-hero' : ''}
      style={{ 
        animationDelay: `${fadeIn.delay}s`,
        opacity: fadeIn.isVisible ? undefined : 0
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
};

// CTA wrapper component for fade-in
const ProductsCTAFadeIn: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const fadeIn = useFadeIn<HTMLDivElement>({ delay: 0.2 });
  return (
    <div 
      ref={fadeIn.ref} 
      className={fadeIn.isVisible ? 'fade-in-hero' : ''}
      style={{ 
        animationDelay: `${fadeIn.delay}s`,
        opacity: fadeIn.isVisible ? undefined : 0
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
};

const ProductsPage: React.FC<ProductsPageProps> = ({ onLoginClick }) => {
  const [hoveredApi, setHoveredApi] = useState<number | null>(null);
  const headerFadeIn = useFadeIn<HTMLDivElement>({ delay: 0.1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'trust-score',
      title: 'Trust Score',
      subtitle: 'Recommended',
      description: 'Comprehensive analysis combining all detection methods to generate an overall trustworthiness score.',
      apis: [
        {
          id: 6,
          name: 'Trust Score',
          description: 'Combine all detection methods to generate an overall trustworthiness score.',
          endpoint: '/api/trust-score',
          icon: Zap,
          color: '#b91c5c',
          features: [
            'Complete profile analysis',
            'Multi-image and text support',
            'Unified trust score'
          ],
          useCase: 'Get complete profile trust assessment'
        }
      ]
    },
    {
      id: 'single-image',
      title: 'Single Image',
      subtitle: 'Individual image analysis',
      description: 'Analyze single images with face detection and AI-generated content identification.',
      apis: [
        {
          id: 1,
          name: 'Face Detection',
          description: 'Detect faces in images with detailed analysis including face count, confidence scores, and bounding box coordinates.',
          endpoint: '/api/face-detect',
          icon: Target,
          color: '#6366f1',
          features: [
            'Face count detection',
            'Confidence scoring',
            'High accuracy analysis'
          ],
          useCase: 'Ensure user\'s profile photos contain faces'
        },
        {
          id: 3,
          name: 'AI Image Detection',
          description: 'Identify AI-generated or synthetic images using advanced machine learning models.',
          endpoint: '/api/ai-image-detect',
          icon: Brain,
          color: '#b91c5c',
          features: [
            'AI image detection',
            'Deepfake identification',
            'High precision models'
          ],
          useCase: 'Detect AI-generated profile photos'
        }
      ]
    },
    {
      id: 'multi-image',
      title: 'Multi-Image',
      subtitle: 'Batch processing',
      description: 'Process multiple images simultaneously for efficient analysis across large datasets.',
      apis: [
        {
          id: 2,
          name: 'Batch Face Detection',
          description: 'Process multiple images simultaneously for face detection.',
          endpoint: '/api/face-detect-batch',
          icon: Layers,
          color: '#667eea',
          features: [
            'Batch processing',
            'Up to 10 images per call',
            'Parallel analysis'
          ],
          useCase: 'Process multiple profile images at once'
        },
        {
          id: 4,
          name: 'Composite Check',
          description: 'Combine face detection and AI image detection into a single request.',
          endpoint: '/api/composite-check',
          icon: Activity,
          color: '#6366f1',
          features: [
            'Multi-method analysis',
            'Combined results',
            'Single API call'
          ],
          useCase: 'Run multiple checks simultaneously'
        }
      ]
    },
    {
      id: 'sentiment',
      title: 'Sentiment',
      subtitle: 'Text analysis',
      description: 'Analyze text content to detect authenticity and identify suspicious patterns.',
      apis: [
        {
          id: 5,
          name: 'Text Analysis',
          description: 'Analyze bio text, prompts, and metadata to detect authenticity and identify suspicious patterns.',
          endpoint: '/api/text-analysis',
          icon: FileText,
          color: '#667eea',
          features: [
            'Bio and prompts analysis',
            'Suspicous text detection',
            'Authenticity scoring'
          ],
          useCase: 'Analyze profile bios and prompts'
        }
      ]
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const isMobile = window.innerWidth <= 768;
      const navbarOffset = isMobile ? 80 : 120;
      // Use getBoundingClientRect for more accurate positioning
      const rect = element.getBoundingClientRect();
      const targetPosition = window.pageYOffset + rect.top - navbarOffset;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 1200; // 1.2 seconds for slower, smoother scroll
      let start: number | null = null;

      const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animation = (currentTime: number) => {
        if (start === null) start = currentTime;
        const timeElapsed = currentTime - start;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = easeInOutCubic(progress);
        
        window.scrollTo(0, startPosition + distance * ease);
        
        if (timeElapsed < duration) {
          requestAnimationFrame(animation);
        }
      };

      requestAnimationFrame(animation);
    }
  };

  return (
    <section id="products" className="products-page">
      <div className="products-container">
        <div 
          ref={headerFadeIn.ref} 
          className={`products-header ${headerFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ 
            animationDelay: `${headerFadeIn.delay}s`,
            opacity: headerFadeIn.isVisible ? undefined : 0
          } as React.CSSProperties}
        >
          <div className="products-header-content">
            <div className="products-icon-wrapper">
              <Package className="products-icon" />
            </div>
            <div>
              <h1 className="products-title">Our APIs</h1>
              <div className="table-of-contents">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    className="toc-item"
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {sections.map((section, sectionIndex) => (
          <SectionFadeIn key={section.id} delay={0.1 + sectionIndex * 0.1}>
            <div id={section.id} className="api-section">
            <div className="section-header">
              <h2 className="section-title">{section.title}</h2>
              {section.subtitle && (
                <p className="section-subtitle">{section.subtitle}</p>
              )}
            </div>

            <div className={`apis-grid ${section.apis.length === 1 ? 'single-item' : section.apis.length === 2 ? 'two-items' : ''}`}>
              {section.apis.map((api, index) => {
                const IconComponent = api.icon;
                const globalIndex = sectionIndex * 10 + index;
                return (
                  <div
                    key={api.id}
                    className={`api-card ${hoveredApi === api.id ? 'hovered' : ''}`}
                    onMouseEnter={() => setHoveredApi(api.id)}
                    onMouseLeave={() => setHoveredApi(null)}
                    style={{ '--delay': globalIndex * 0.1 } as React.CSSProperties}
                  >
                    <div className="api-card-background"></div>
                    <div className="api-icon-wrapper" style={{ '--api-color': api.color } as React.CSSProperties}>
                      <IconComponent size={24} />
                    </div>
                    <div className="api-card-content">
                      <div className="api-card-header">
                        <h3 className="api-name">{api.name}</h3>
                        <p className="api-description">{api.description}</p>
                      </div>
                      
                      <div className="api-card-details">
                        <div className="api-features">
                          <h4 className="features-title">Features</h4>
                          <ul className="features-list">
                            {api.features.map((feature, idx) => (
                              <li key={idx} className="feature-item">
                                <CheckCircle size={14} />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="api-divider"></div>

                        <div className="api-use-case">
                          <span className="use-case-label">Use Case</span>
                          <p className="use-case-text">{api.useCase}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </SectionFadeIn>
        ))}

        <ProductsCTAFadeIn>
          <div className="products-cta">
            <button className="get-started-btn" onClick={onLoginClick}>
              Get Started
              <ChevronRight size={20} />
            </button>
          </div>
        </ProductsCTAFadeIn>
      </div>
    </section>
  );
};

export default ProductsPage;

